import React, { useState, useEffect, useRef, memo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAdminEmailTracking } from 'data/repositories/adminEmailTrackingRepository';
import { useQueueMonitor } from 'data/repositories/queueMonitorRepository';
import { showToast } from 'data/reducers/toastReducer';
import { useDispatch } from 'react-redux';
import Spinner from 'view/components/Spinner';
import Paginator from 'view/components/Paginator';
import EmailStatistics from './EmailStatistics';
import DeliveryStatisticsCards from './DeliveryStatisticsCards';
import EmailFilters from './EmailFilters';
import EmailTable from './EmailTable';
import EmailDetailsModal from './EmailDetailsModal';
import OutboxHealthCards from './OutboxHealthCards';
import { filtersFromSearch, searchWithoutLinkFilters } from './emailTrackingLinks';
import './AdminEmailTracking.css';
import AdminLayout from '../AdminLayout';
import { usePageTitle } from 'utils/pageTitle';

// The filters the page starts from, and returns to on reset.
const DEFAULT_FILTERS = {
	status: 'all',
	type: 'all',
	sms_status: 'all',
	delivery_status: 'all',
	search: '',
	date_from: '',
	date_to: '',
	older_than_hours: '',
	sort_by: 'created_at',
	sort_order: 'desc',
};

const AdminEmailTracking = memo(() => {
	usePageTitle('Admin | Email Tracking');
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();

	// The email outbox snapshot behind the outbox health cards (null on a
	// backend without the endpoint, which hides them)
	const { outbox, actions: queueMonitorActions } = useQueueMonitor();

	const {
		items: emails,
		isLoading,
		pagination,
		actions,

		statistics,
		filterOptions,
	} = useAdminEmailTracking();

	// Local state for filters, starting from any a deep link carries
	const [filters, setFilters] = useState(() => ({
		...DEFAULT_FILTERS,
		...filtersFromSearch(location.search),
	}));

	// A later deep link to this page (an outbox card, say) replaces the
	// filters. The URL the page opened with is already in the initial state,
	// so it is skipped here; setting it again would fetch the list twice.
	const appliedSearch = useRef(location.search);
	useEffect(() => {
		if (location.search === appliedSearch.current) {
			return;
		}
		appliedSearch.current = location.search;
		const linked = filtersFromSearch(location.search);
		if (Object.keys(linked).length > 0) {
			setFilters({ ...DEFAULT_FILTERS, ...linked });
		}
	}, [location.search]);

	// Once the user searches, resets or sorts, the deep link's filters leave
	// the URL, so a reload or the same link again matches what is applied.
	const dropLinkFiltersFromUrl = () => {
		const search = searchWithoutLinkFilters(location.search);
		if (search !== location.search) {
			history.replace({ pathname: location.pathname, search });
		}
	};

	useEffect(() => {
		queueMonitorActions.getOutbox();
	}, [queueMonitorActions]);

	// Statistics state
	const [isLoadingStats] = useState(false);
	const [statsDays, setStatsDays] = useState(7);

	// Modal state
	const [selectedEmail, setSelectedEmail] = useState(null);
	const [showDetailsModal, setShowDetailsModal] = useState(false);

	// Cleanup state
	const [isCleaning, setIsCleaning] = useState(false);

	// Fetch emails on mount and when filters change
	useEffect(() => {
		actions.fetchPaginated(1, 25, filters);
	}, [actions, filters]);

	// Fetch statistics on mount
	useEffect(() => {
		actions.getStatistics({ days: statsDays });
		actions.getFilterOptions();
	}, [actions, statsDays]);

	// Handle filter change
	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	// Handle search
	const handleSearch = (nextFilters) => {
		dropLinkFiltersFromUrl();
		setFilters((prev) => ({
			...prev,
			...nextFilters,
		}));
	};

	// Handle reset filters
	const handleResetFilters = () => {
		dropLinkFiltersFromUrl();
		setFilters(DEFAULT_FILTERS);
	};

	// Handle page change
	const handlePageChange = (page) => {
		actions.fetchPaginated(page, pagination.perPage, filters);
	};

	// Handle sort
	const handleSort = (column) => {
		const newOrder =
			filters.sort_by === column && filters.sort_order === 'asc'
				? 'desc'
				: 'asc';

		dropLinkFiltersFromUrl();
		setFilters((prev) => ({
			...prev,
			sort_by: column,
			sort_order: newOrder,
		}));
	};

	// Handle view details
	const handleViewDetails = (email) => {
		try {
			// actions.fetchOne(email.id);
			// setSelectedEmail(response.data.data);
			setSelectedEmail(email);
			setShowDetailsModal(true);
		} catch (error) {
			dispatch(
				showToast({
					title: 'Error',
					body: 'Failed to fetch email details',
					type: 'warning',
				}),
			);
		}
	};

	// Handle retry email
	const handleRetry = async (id) => {
		if (!window.confirm('Are you sure you want to retry this email?')) {
			return;
		}

		try {
			await actions.retryEmail({ id }).then(() => {
				dispatch(
					showToast({
						title: 'Success',
						body: 'Email queued for retry',
						type: 'success',
					}),
				);
			});

			// Refresh list
			actions.fetchPaginated(
				pagination.currentPage,
				pagination.perPage,
				filters,
			);
		} catch (error) {
			dispatch(
				showToast({
					title: 'Error',
					body: error.response?.data?.error || 'Failed to retry email',
					type: 'warning',
				}),
			);
		}
	};

	// Handle cleanup
	const handleCleanup = async () => {
		const days = window.prompt('Delete emails older than how many days?', '90');

		if (!days || isNaN(days)) {
			return;
		}

		if (
			!window.confirm(
				`Are you sure you want to delete all sent/failed emails older than ${days} days? This cannot be undone.`,
			)
		) {
			return;
		}

		setIsCleaning(true);
		try {
			const response = await actions.cleanup({ days: parseInt(days) });
			dispatch(
				showToast({
					title: 'Success',
					body: response.data.message,
					type: 'success',
				}),
			);
			// Refresh list
			actions.fetchPaginated(1, pagination.perPage, filters);
		} catch (error) {
			dispatch(
				showToast({
					title: 'Error',
					body: 'Failed to cleanup emails',
					type: 'warning',
				}),
			);
		} finally {
			setIsCleaning(false);
		}
	};

	// Handle refresh statistics
	const handleRefreshStats = () => {
		actions.getStatistics({ days: statsDays });
	};

	return (
		<AdminLayout>
			<div className="admin-email-tracking">
				<div className="page-header">
					<div>
						<h1>Email Tracking</h1>
						<p className="text-muted">
							Monitor email and SMS delivery status and troubleshoot failures
						</p>
					</div>
					<div className="page-header-actions">
						<button
							className="btn btn-secondary"
							onClick={handleCleanup}
							disabled={isCleaning}
						>
							{isCleaning ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" />
									Cleaning...
								</>
							) : (
								<>
									<i className="bi bi-trash me-2" />
									Cleanup Old Emails
								</>
							)}
						</button>
					</div>
				</div>

				{/* SMS Statistics Section */}
				{statistics?.sms && (
					<div className="row g-3 mb-4">
						<div className="col-md-3">
							<div className="card">
								<div className="card-body">
									<p className="text-muted mb-1">SMS Sent</p>
									<h4 className="mb-0 text-success">
										{(statistics.sms.sent || 0).toLocaleString()}
									</h4>
								</div>
							</div>
						</div>
						<div className="col-md-3">
							<div className="card">
								<div className="card-body">
									<p className="text-muted mb-1">SMS Digest</p>
									<h4 className="mb-0 text-info">
										{(statistics.sms.sent_digest || 0).toLocaleString()}
									</h4>
								</div>
							</div>
						</div>
						<div className="col-md-3">
							<div className="card">
								<div className="card-body">
									<p className="text-muted mb-1">SMS Failed</p>
									<h4 className="mb-0 text-danger">
										{(statistics.sms.failed || 0).toLocaleString()}
									</h4>
								</div>
							</div>
						</div>
						<div className="col-md-3">
							<div className="card">
								<div className="card-body">
									<p className="text-muted mb-1">Alerts with no SMS</p>
									<h4 className="mb-0 text-muted">
										{(statistics.sms.none || 0).toLocaleString()}
									</h4>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Statistics Section */}
				{false && (
					<EmailStatistics
						statistics={statistics}
						isLoading={isLoadingStats}
						statsDays={statsDays}
						onDaysChange={setStatsDays}
						onRefresh={handleRefreshStats}
					/>
				)}

				{/* Email outbox health: the numbers the queue monitor's outbox rules page on */}
				<OutboxHealthCards outbox={outbox} />

				{/* What Amazon SES reported, on the page itself: the statistics section above is switched off */}
				<DeliveryStatisticsCards stats={statistics?.stats} />

				{/* Filters Section */}
				<EmailFilters
					filters={filters}
					filterOptions={filterOptions}
					onFilterChange={handleFilterChange}
					onSearch={handleSearch}
					onReset={handleResetFilters}
				/>

				{/* Table Section */}
				<div className="card">
					<div className="card-header">
						<h5 className="mb-0">
							Email History
							{pagination.total > 0 && (
								<span className="badge bg-secondary ms-2">
									{pagination.total.toLocaleString()} total
								</span>
							)}
						</h5>
					</div>
					<div className="card-body p-0">
						{isLoading ? (
							<div className="text-center py-5">
								<Spinner />
								<p className="text-muted mt-3">Loading emails...</p>
							</div>
						) : emails.length === 0 ? (
							<div className="text-center py-5">
								<i
									className="bi bi-inbox"
									style={{ fontSize: '3rem', opacity: 0.3 }}
								/>
								<p className="text-muted mt-3">No emails found</p>
								<button
									className="btn btn-sm btn-primary"
									onClick={handleResetFilters}
								>
									Clear Filters
								</button>
							</div>
						) : (
							<>
								<EmailTable
									emails={emails}
									sortBy={filters.sort_by}
									sortOrder={filters.sort_order}
									onSort={handleSort}
									onViewDetails={handleViewDetails}
									onRetry={handleRetry}
								/>

								{/* Pagination */}
								{pagination.lastPage > 1 && (
									<div className="p-3 border-top">
										<Paginator
											totalPages={pagination.lastPage}
											currentPage={pagination.currentPage}
											lastPage={pagination.lastPage}
											setPage={handlePageChange}
										/>
										<div className="text-center text-muted mt-2">
											<small>
												Showing {pagination.from} to {pagination.to} of{' '}
												{pagination.total} emails
											</small>
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{/* Email Details Modal */}
				{showDetailsModal && selectedEmail && (
					<EmailDetailsModal
						email={selectedEmail}
						onClose={() => {
							setShowDetailsModal(false);
							setSelectedEmail(null);
						}}
						onRetry={handleRetry}
					/>
				)}
			</div>
		</AdminLayout>
	);
});

export default AdminEmailTracking;
